import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { useEstimateModal } from '../EstimateModalContext';

const HeroSection = () => {
  const { openModal } = useEstimateModal();

  const trustBadges = [
    { name: 'NETCC Approved Seller', useShield: true },
    { name: 'Solar Victoria Approved', useShield: false },
    { name: 'Accredited Installers', useShield: false }
  ];

  return (
    <section className="relative flex items-center overflow-hidden min-h-[600px] md:min-h-[660px]">
      {/* Background — real Supa Energy Hub install team */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage: `url('/team/supa-team-install.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
        }}
        data-testid="hero-team-bg"
      />

      {/* Gradient Overlay — strong on the left for text, clears on the right so the
          Supa Energy Hub logos on the team's backs stay visible */}
      <div className="absolute inset-0 bg-[#101a14]/60 md:bg-gradient-to-r md:from-[#101a14]/88 md:via-[#101a14]/50 md:to-transparent" />

      {/* Accent glow */}
      <div className="absolute -top-10 left-0 w-96 h-96 bg-[#B5D43B]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative z-10 w-full">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#B5D43B]" />
            <span className="text-[11px] font-bold tracking-widest uppercase text-[#B5D43B]">Our own accredited install team</span>
          </span>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-4 md:mb-5">
            Trusted Experts in <span className="text-[#B5D43B]">Home Energy</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base md:text-lg text-white/85 max-w-xl mb-5 md:mb-6 leading-relaxed">
            From solar panels to batteries, heat pumps to EV chargers — we specialise in{' '}
            <span className="font-bold text-[#B5D43B]">complete home electrification</span>,
            helping Australian homeowners reduce energy bills, eliminate gas dependency, and
            future-proof their homes.
          </p>

          {/* 5-star inline trust */}
          <div className="flex items-center gap-2.5 mb-5" data-testid="hero-5star">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-[#FBBC05] text-[#FBBC05]" />
              ))}
            </div>
            <span className="text-sm md:text-base font-semibold text-white">5.0 Google Reviews from Happy Homeowners</span>
          </div>

          {/* Trust Points with Icons */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm px-2.5 md:px-3 py-1.5 md:py-2 rounded-full border border-white/20">
                {badge.useShield ? (
                  <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#B5D43B]" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#B5D43B]" />
                )}
                <span className="text-xs md:text-sm font-medium text-white">{badge.name}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-start">
            <Button
              onClick={openModal}
              className="btn-primary gap-2 text-sm md:text-base px-6 md:px-8 py-3 md:py-4 h-auto w-full sm:w-auto flex items-center"
            >
              <span className="flex flex-col items-start leading-tight">
                <span>Get a Free Estimate</span>
                <span className="text-[10px] md:text-xs opacity-80 font-normal">Takes only 30 secs</span>
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Link to="/services">
              <Button className="bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-sm text-sm md:text-base px-6 md:px-8 py-3 md:py-4 h-auto w-full sm:w-auto">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
